/**
 * Ahrefs Error Parser Module (Story 20.1, AC #13)
 *
 * Two-tier error mapping (carry-forward from Reddit 19.10 + Amazon Ads 19.13
 * fragile-regex hardening):
 *  1. Primary — structured-code match on `response.body.error` field.
 *  2. Secondary — HTTP status code + message-substring regex fallback.
 *
 * Why two tiers: Ahrefs returns an unstructured error envelope on most failures
 * (just a status code + a free-text message). Hard-coding regex patterns
 * against message strings is fragile. We try the structured code first; only
 * if that fails do we fall back to status + message-pattern.
 *
 * Sanitization: error messages MUST flow through `sanitizeAhrefsErrorMessage`
 * before reaching any log line or `AdapterError.message`. This redacts both
 * the generic token-shape patterns (via `sanitizeErrorMessage` from
 * safe-fetch) AND the literal decrypted Ahrefs API key when supplied. The
 * decrypted key is in scope for the entire `fetchMetrics` lifetime — defensive
 * redaction prevents accidental log-leaks.
 *
 * See AC #13 in the Story 20.1 spec.
 */

import type { AdapterErrorCode } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Types
// ============================================================================

/**
 * Closed enumeration of Ahrefs error types we map to. The `unknown` arm
 * exists so the parser never throws on unexpected payload shapes — the
 * caller can still surface a sanitized provider message even when no
 * structured code matched.
 */
export type AhrefsErrorType =
  | "unauthorized" // 401 — invalid Bearer / revoked / expired key
  | "forbidden" // 403 — plan-tier gate, brand-radar gate, etc.
  | "not_found" // 404 — target domain not in Ahrefs' index
  | "validation_error" // 400 — malformed request (bad `select`, missing `mode`, etc.)
  | "rate_limit_exceeded" // 429 — per-key RPS cap
  | "provider_unavailable" // 5xx — Ahrefs regional outage
  | "unknown";

export interface AhrefsApiError {
  readonly type: AhrefsErrorType;
  readonly message: string;
  readonly status: number | null;
  readonly retryable: boolean;
}

interface AhrefsErrorEnvelope {
  readonly error?: string;
  readonly message?: string;
  readonly detail?: string;
  readonly errors?: ReadonlyArray<{ readonly message?: string; readonly code?: string }>;
}

// ============================================================================
// Static maps
// ============================================================================

/**
 * Map structured `error` codes returned by Ahrefs to our internal type. This
 * is the primary path; `parseAhrefsApiError` walks this first.
 */
const STRUCTURED_CODE_MAP: Record<string, AhrefsErrorType> = {
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  not_found: "not_found",
  validation_error: "validation_error",
  rate_limit_exceeded: "rate_limit_exceeded",
};

/**
 * Map HTTP status codes to our internal type. Used as the fallback path
 * when no structured code matches, AND as the primary anchor for 5xx codes
 * (Ahrefs typically does not return a structured code on transient outages).
 */
const STATUS_TO_TYPE: Record<number, { type: AhrefsErrorType; retryable: boolean }> = {
  400: { type: "validation_error", retryable: false },
  401: { type: "unauthorized", retryable: false },
  403: { type: "forbidden", retryable: false },
  404: { type: "not_found", retryable: false },
  // Story 20.2 AC #10 — Ahrefs returns 422 (Unprocessable Entity) for
  // semantically valid requests that violate a plan-imposed constraint
  // (e.g. organic-keywords `date` outside the customer's history window).
  // Distinct from 400 (malformed-request) because the message guidance
  // differs ("upgrade plan" vs "fix request shape").
  422: { type: "validation_error", retryable: false },
  429: { type: "rate_limit_exceeded", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

/**
 * Map our internal type to the AdapterErrorCode the adapter contract uses.
 * Note: 403 maps to `permission_denied` because Ahrefs uses 403 for both
 * "key valid but plan insufficient" AND "key valid but endpoint requires
 * Brand Radar add-on" — both are remedied by the customer upgrading. The
 * cron's `adapterErrorToCronAction` routes both to `mark_revoked_notify_user`.
 */
const TYPE_TO_ADAPTER_CODE: Record<AhrefsErrorType, AdapterErrorCode> = {
  unauthorized: "token_expired",
  forbidden: "permission_denied",
  not_found: "account_not_found",
  validation_error: "invalid_request",
  rate_limit_exceeded: "rate_limited",
  provider_unavailable: "provider_unavailable",
  unknown: "invalid_response",
};

/**
 * Status-code retryability flag for the unknown-type fallback. Used only when
 * `STATUS_TO_TYPE` does not have an entry for the code (very rare —
 * effectively any 4xx outside the 400/401/403/404/429 set).
 */
function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

// ============================================================================
// Plan-tier message regex (secondary path — AC #13)
// ============================================================================

/**
 * Regex matching common message substrings that distinguish plan-tier-related
 * 403s from per-resource 403s. When the body matches this pattern AND the
 * status is 403, we are confident the 403 is "your plan does not include this
 * feature" (vs. "this specific account doesn't have access").
 *
 * Both map to the same AdapterErrorCode (`permission_denied`) — the regex is
 * informational and used to enrich the surfaced message, not to redirect
 * routing.
 */
const PLAN_TIER_REGEX = /\b(plan|upgrade|enterprise|subscription|tier)\b/i;

// ============================================================================
// Story 20.2 — Rank Tracker / Organic Keywords specialized regex (AC #10)
// ============================================================================

/**
 * 403 sub-pattern: "your plan doesn't include the Rank Tracker add-on". Maps
 * to `permission_denied` with a Rank-Tracker-specific user-facing message.
 *
 * The regex matches both "rank tracker" and "rank-tracker" (with optional
 * dash/space) AND requires the message also reference subscription/access —
 * defensive against false positives on generic permission_denied 403s.
 */
const RANK_TRACKER_SUBSCRIPTION_REGEX =
  /rank.?tracker.*(subscription|access|add.?on)|no.*active.*rank.?tracker/i;

/**
 * 404 sub-pattern: a Rank Tracker `project_id` was not found in the
 * customer's workspace. Distinct from a generic 404 because the customer
 * needs to re-pick a project (the widget UI surfaces a banner on this code
 * variant).
 */
const RANK_TRACKER_PROJECT_NOT_FOUND_REGEX = /project.*not.*found/i;

/**
 * 422 sub-pattern: organic-keywords date is outside the customer's plan
 * history window. Enterprise plans have full history; lower tiers cap at
 * 6/12/24 months. Maps to `invalid_request` with a plan-tier-aware message.
 */
const DATE_OUT_OF_RANGE_REGEX = /date.*out.?of.?range|exceeds.*history/i;

// ============================================================================
// Story 20.2e — Brand Radar add-on subscription regex (AC #9)
// ============================================================================

/**
 * 403 sub-pattern: "your plan doesn't include the Brand Radar add-on". Maps
 * to `permission_denied` with a Brand-Radar-specific actionable message.
 *
 * Brand Radar is a paid add-on layered on top of any paid Ahrefs subscription
 * ($199/month per AI platform, $699/month for all 7 platforms — verified
 * 2026-05-05 via [help.ahrefs.com/en/articles/11064852-about-brand-radar]).
 * NOT bundled with Enterprise — most Enterprise customers do NOT have BR.
 *
 * **Specificity ordering invariant (AC #9):** this regex MUST be checked
 * BEFORE the generic plan-tier regex (`PLAN_TIER_REGEX`). A 403 message that
 * matches both should produce the BR-specific copy, not the generic plan-tier
 * copy. The test harness verifies this invariant.
 *
 * The pattern requires both "brand radar" / "brand-radar" mention AND a
 * subscription/access keyword — defensive against false positives on generic
 * 403s that happen to mention Brand Radar in passing (e.g. an unrelated
 * "Brand Radar coming soon" message).
 *
 * **Code Review Pass 2 (L4) — backtracking-safe bounded quantifiers:** the
 * unbounded `.*` from the original draft could hit polynomial-time
 * backtracking on adversarial messages (10KB error body containing
 * `brand-radar` followed by repeating `not not not...`). Bounded `[\s\S]{0,200}`
 * caps each `.*` window at 200 chars per segment — Ahrefs error messages are
 * typically under 200 chars, so the bound is a pure safety floor with no
 * impact on legitimate matches.
 */
// Exported (Code Review Pass 3, P3-L7) so the discoverEndpoints probe in
// ahrefs.ts can re-detect BR-flavored 403s using the SAME regex (single
// source of truth). The probe previously used a looser `/brand[ -]?radar/i`
// that risked false-positive subscription_required on unrelated 403s
// mentioning Brand Radar in passing.
export const BRAND_RADAR_SUBSCRIPTION_REGEX =
  /brand.?radar[\s\S]{0,200}(?:subscription|access|add.?on|not\s+available|not\s+enabled)|no\s+active\s+brand.?radar|brand.?radar[\s\S]{0,100}required/i;

// ============================================================================
// Sanitization (AC #13)
// ============================================================================

/**
 * Wraps the generic `sanitizeErrorMessage` helper from safe-fetch with one
 * additional defense: if `decryptedKey` is supplied, every literal occurrence
 * of that key in the message is replaced with `[redacted-ahrefs-key]` BEFORE
 * the generic token-shape redaction runs. This catches the case where the
 * decrypted plaintext API key leaks into a provider error message — Ahrefs
 * sometimes echoes the auth header back in malformed-request 400s.
 *
 * The encrypted form should never appear in error messages, but the decrypted
 * plaintext form is in scope for the entire `fetchMetrics` lifetime — this
 * function exists specifically to defend against that exposure window.
 *
 * @param message - The raw provider error message (potentially containing the key).
 * @param decryptedKey - The decrypted plaintext API key (optional). When provided,
 *   every literal occurrence is replaced with `[redacted-ahrefs-key]`.
 * @returns Sanitized message safe for logging and storage.
 */
export function sanitizeAhrefsErrorMessage(
  message: string,
  decryptedKey?: string
): string {
  let sanitized = message;
  // Real Ahrefs API keys are 80-128 chars per the docs. Short test stubs
  // (single letters like "k") would otherwise match every occurrence of the
  // letter inside human-readable text and corrupt the message. Require a
  // realistic minimum key length before redaction.
  if (decryptedKey && decryptedKey.length >= 16) {
    // Escape regex metacharacters in the key before building the replacement
    // pattern — the API key is opaque user input, not a regex literal.
    const escaped = decryptedKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(new RegExp(escaped, "g"), "[redacted-ahrefs-key]");
  }
  return sanitizeErrorMessage(sanitized);
}

// ============================================================================
// Two-tier parser (AC #13)
// ============================================================================

/**
 * Two-tier Ahrefs error parser.
 *
 * Tier 1 (primary) — match `response.body.error` against `STRUCTURED_CODE_MAP`.
 * Tier 2 (secondary) — fall back to HTTP status + message-substring regex.
 *
 * @param responseBody - Parsed JSON body of the response. May be undefined for
 *   non-JSON responses (Ahrefs occasionally returns plaintext on 5xx).
 * @param httpStatus - HTTP status code from the response.
 * @param decryptedKey - Optional plaintext key for sanitization (AC #13).
 * @returns A structured AhrefsApiError, or null if the status was 2xx (success).
 */
export function parseAhrefsApiError(
  responseBody: unknown,
  httpStatus: number,
  decryptedKey?: string
): AhrefsApiError | null {
  if (httpStatus >= 200 && httpStatus < 300) {
    return null;
  }

  const envelope = (responseBody ?? {}) as AhrefsErrorEnvelope;
  const rawMessage =
    envelope.message ??
    envelope.detail ??
    envelope.errors?.[0]?.message ??
    `Ahrefs API error: HTTP ${httpStatus}`;
  const message = sanitizeAhrefsErrorMessage(rawMessage, decryptedKey);

  // Tier 1 — structured code match
  const structuredType =
    envelope.error !== undefined ? STRUCTURED_CODE_MAP[envelope.error] : undefined;
  if (structuredType) {
    const type = structuredType;
    const statusMapping = STATUS_TO_TYPE[httpStatus];
    return {
      type,
      message,
      status: httpStatus,
      retryable: statusMapping?.retryable ?? isRetryableStatus(httpStatus),
    };
  }

  // Tier 2 — status + message-pattern fallback
  const mapping = STATUS_TO_TYPE[httpStatus];
  if (mapping) {
    // 403 + Brand Radar subscription message — Story 20.2e AC #9. Specificity
    // ordering: this check runs BEFORE the Rank Tracker / generic plan-tier
    // regex. A 403 message that matches multiple regexes should produce the
    // BR-specific copy because BR is a paid add-on (vs. RT which is bundled
    // with Enterprise; vs. generic plan-tier 403s).
    if (httpStatus === 403 && BRAND_RADAR_SUBSCRIPTION_REGEX.test(rawMessage)) {
      return {
        type: "forbidden",
        message: sanitizeAhrefsErrorMessage(
          "Your Ahrefs subscription doesn't include the Brand Radar add-on — enable the add-on at ahrefs.com/brand-radar (starts at $199/month per AI platform) or remove the brand-radar widgets.",
          decryptedKey
        ),
        status: httpStatus,
        retryable: false,
      };
    }
    // 403 + Rank Tracker subscription message — Story 20.2 AC #10. Keep
    // type=`forbidden` (routes to `permission_denied`) but rewrite the
    // user-facing message so the widget surfaces an actionable next step
    // ("Your Ahrefs plan doesn't include Rank Tracker access — upgrade or
    // remove the rank-tracker widgets.").
    if (httpStatus === 403 && RANK_TRACKER_SUBSCRIPTION_REGEX.test(rawMessage)) {
      return {
        type: "forbidden",
        message: sanitizeAhrefsErrorMessage(
          "Your Ahrefs plan doesn't include Rank Tracker access — upgrade or remove the rank-tracker widgets.",
          decryptedKey
        ),
        status: httpStatus,
        retryable: false,
      };
    }
    // 404 + project-not-found message — Story 20.2 AC #10. The widget UI
    // surfaces this via a banner prompting the customer to re-pick a project.
    // Story 20.2d AC #9: same regex catches Site Audit `/projects` /
    // `/issues` / `/page-explorer` / `/page-content` 404 responses (Ahrefs
    // emits the same "Project not found" body shape across surfaces per
    // docs verification 2026-05-05). Message generalized in Code Review
    // Pass 2 to cover both Rank Tracker and Site Audit projects.
    if (httpStatus === 404 && RANK_TRACKER_PROJECT_NOT_FOUND_REGEX.test(rawMessage)) {
      return {
        type: "not_found",
        message: sanitizeAhrefsErrorMessage(
          "The Ahrefs project ID was not found in your workspace — verify the project still exists in Ahrefs Rank Tracker or Site Audit.",
          decryptedKey
        ),
        status: httpStatus,
        retryable: false,
      };
    }
    // 422 + date-out-of-range message — Story 20.2 AC #10. Plan-tier-aware
    // copy directs the customer toward the upgrade path.
    if (httpStatus === 422 && DATE_OUT_OF_RANGE_REGEX.test(rawMessage)) {
      return {
        type: "validation_error",
        message: sanitizeAhrefsErrorMessage(
          "Requested date is outside your Ahrefs plan's history window — Enterprise plans have full history; lower tiers are limited.",
          decryptedKey
        ),
        status: httpStatus,
        retryable: false,
      };
    }
    // 403 + plan-tier message → keep `forbidden` but the message will already
    // carry plan-tier context via `rawMessage`. We do NOT remap the type here —
    // routing is identical (`permission_denied`) and the regex is purely
    // informational. We DO retain the regex so future enhancements (e.g. a
    // user-facing notification) can surface a tailored copy.
    if (httpStatus === 403 && PLAN_TIER_REGEX.test(rawMessage)) {
      return {
        type: "forbidden",
        message,
        status: httpStatus,
        retryable: false,
      };
    }
    return {
      type: mapping.type,
      message,
      status: httpStatus,
      retryable: mapping.retryable,
    };
  }

  // Unknown status — preserve the raw message but mark retryable by status
  return {
    type: "unknown",
    message,
    status: httpStatus,
    retryable: isRetryableStatus(httpStatus),
  };
}

/**
 * Map an `AhrefsErrorType` to the canonical `AdapterErrorCode` used by the
 * adapter contract.
 */
export function ahrefsErrorTypeToAdapterCode(type: AhrefsErrorType): AdapterErrorCode {
  return TYPE_TO_ADAPTER_CODE[type];
}

// ============================================================================
// Throwable wrapper (mirrors Pinterest/Reddit/Amazon `*ApiCallError`)
// ============================================================================

/**
 * Throwable error class used by the safe-fetch retry layer when an Ahrefs
 * call fails with a non-success status. Carries the parsed structured error
 * so the adapter can map it to the right `AdapterErrorCode` without re-parsing.
 *
 * Mirrors `RedditAdsApiCallError` / `AmazonAdsApiCallError` / etc.
 */
export class AhrefsApiCallError extends Error {
  readonly status: number;
  readonly type: AhrefsErrorType;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(parsed: AhrefsApiError, retryAfterMs?: number) {
    super(parsed.message);
    this.name = "AhrefsApiCallError";
    this.status = parsed.status ?? 0;
    this.type = parsed.type;
    this.retryable = parsed.retryable;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Parse an Ahrefs `Retry-After` header value. AC #5 — Ahrefs may return either
 * integer seconds OR HTTP-date format per RFC 7231; both are handled.
 *
 * Bounds:
 *  - Floored at 1000ms (1s) — prevents busy-loop on a 0/negative value.
 *  - Capped at 60_000ms (60s) — prevents pathological multi-minute sleeps on
 *    a malicious or misconfigured server.
 *
 * @returns milliseconds to wait before retry, or undefined if missing/unparseable
 *   (the safeFetch wrapper falls back to its exponential backoff in that case).
 */
export function parseAhrefsRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;

  const trimmed = headerValue.trim();
  if (trimmed.length === 0) return undefined;

  // Try integer seconds first
  const seconds = Number.parseInt(trimmed, 10);
  if (!Number.isNaN(seconds) && /^\d+$/.test(trimmed)) {
    const ms = seconds * 1000;
    return Math.max(1000, Math.min(60_000, ms));
  }

  // Try HTTP-date
  const date = new Date(trimmed);
  if (!Number.isNaN(date.getTime())) {
    const ms = date.getTime() - Date.now();
    if (ms <= 0) return 1000; // past date → wait the minimum
    return Math.max(1000, Math.min(60_000, ms));
  }

  return undefined;
}
